import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback } from 'react';
import type { EnvSchema } from '../EnvSelector';
import { derivePk } from '../lib/derive-pk';
import { useLocaleStore } from '../lib/locale-store';
import { RELATIONSHIPS } from '../lib/relationships';
import { translateKey } from '../lib/translations';
import { buildChildCacheKey, buildParentCacheKey, useFkExpand } from '../lib/use-fk-expand';
import type { TableName } from '../TableSidebar';
import { DataTable } from './DataTable';
import { ExpandPanel, type ExpandSection } from './ExpandPanel';

type RowMap = Record<string, unknown>;

interface Props {
  env: EnvSchema;
  table: TableName;
  rows: RowMap[];
}

/**
 * Read-only nested view of a related table.
 *
 * Used to render parent / child records discovered via `RELATIONSHIPS[table]`.
 * Each row gets a leftmost ▶ that toggles a children panel and any expanded
 * parent FKs; FK columns themselves render an inline ▶ next to the value to
 * lazy-fetch and inline the referenced row(s).
 *
 * The component recurses through itself so the expansion tree can go arbitrary
 * depth — every level uses the same hook for its own state.
 */
export function NestedTable({ env, table, rows }: Props) {
  const fk = useFkExpand(env);
  const localeMode = useLocaleStore((s) => s.mode);
  // Subscribe to dataVersion so cells re-render after translations CDN fetch
  // resolves (the cache is module-level and not reactive on its own).
  useLocaleStore((s) => s.dataVersion);
  const rowKey = useCallback((row: RowMap) => JSON.stringify(derivePk(row, table)), [table]);

  if (rows.length === 0) {
    return <div className="text-xs text-muted-foreground italic">no rows</div>;
  }

  const sample = rows[0];
  const rels = RELATIONSHIPS[table];
  const parentFKByColumn = new Map(rels.parents.map((p) => [p.column, p]));

  // Build read-only columns from the sample row keys (skip `updated_at`).
  const dataColumns: ColumnDef<RowMap>[] = Object.keys(sample)
    .filter((c) => c !== 'updated_at')
    .map((col) => {
      const parentFK = parentFKByColumn.get(col);
      return {
        accessorKey: col,
        header: col,
        cell: (ctx: CellContext<RowMap, unknown>) => {
          const v = ctx.getValue();
          if (v === null || v === undefined) {
            return <span className="text-muted-foreground">NULL</span>;
          }
          const display = typeof v === 'object' ? JSON.stringify(v) : String(v);

          const hasFkValue = parentFK
            ? parentFK.isArray
              ? Array.isArray(v) && (v as unknown[]).length > 0
              : v != null
            : false;

          if (parentFK && hasFkValue) {
            const rowK = rowKey(ctx.row.original);
            const cacheKey = buildParentCacheKey(rowK, parentFK.column);
            const isOpen = fk.parentExpanded.has(cacheKey);
            return (
              <span className="inline-flex items-center gap-1">
                <span>{display}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={async () => {
                    if (!isOpen) await fk.loadParent(rowK, parentFK, v);
                    fk.toggleParentExpanded(cacheKey);
                    fk.ensureRowExpanded(rowK);
                  }}
                >
                  {isOpen ? (
                    <ChevronDown className="h-3 w-3 inline" />
                  ) : (
                    <ChevronRight className="h-3 w-3 inline" />
                  )}
                </button>
              </span>
            );
          }
          if (typeof v === 'string' && localeMode !== 'KEY') {
            const translated = translateKey(v, localeMode === 'EN' ? 'en' : 'ko');
            return (
              <span className="text-xs" title={v}>
                {translated}
              </span>
            );
          }
          return <span className="text-xs">{display}</span>;
        },
      };
    });

  const expandColumn: ColumnDef<RowMap> = {
    id: '_expand',
    header: '',
    size: 32,
    cell: ({ row }) => {
      const k = rowKey(row.original);
      const isOpen = fk.expanded.has(k);
      if (rels.children.length === 0 && rels.parents.length === 0) return null;
      return (
        <button
          type="button"
          onClick={() => fk.toggleRowExpanded(k)}
          className="hover:bg-muted rounded p-0.5"
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      );
    },
  };

  const finalColumns = [expandColumn, ...dataColumns];

  return (
    <DataTable
      columns={finalColumns}
      data={rows}
      expandedRows={fk.expanded}
      rowKey={rowKey}
      renderSubRow={(row) => {
        const rowK = rowKey(row);
        const parentExpansions = rels.parents
          .filter((p) => fk.parentExpanded.has(buildParentCacheKey(rowK, p.column)))
          .map((p) => {
            const cacheKey = buildParentCacheKey(rowK, p.column);
            const parentRows = fk.parentCache[cacheKey] ?? [];
            const isLoading = fk.parentLoading.has(cacheKey);
            return (
              <div
                key={cacheKey}
                className="my-1 border-l-2 border-blue-300 dark:border-blue-700 pl-2"
              >
                <div className="text-xs text-muted-foreground mb-1">
                  parent: {p.refTable} ({p.column})
                </div>
                {isLoading ? (
                  <div className="text-xs italic">loading…</div>
                ) : parentRows.length === 0 ? (
                  <div className="text-xs italic">not found</div>
                ) : (
                  <NestedTable env={env} table={p.refTable} rows={parentRows} />
                )}
              </div>
            );
          });

        const childSections: ExpandSection[] = rels.children.map((c) => {
          const k = buildChildCacheKey(rowK, c.table);
          const cached = fk.childCache[k];
          return {
            key: k,
            label: `→ ${c.label} (${c.table})`,
            count: cached?.length,
            loaded: !!cached,
            loading: fk.loadingChildren.has(k),
            load: () => fk.loadChildren(rowK, row, c),
            render: () => <NestedTable env={env} table={c.table} rows={cached ?? []} />,
            refresh: () => fk.refreshChildren(rowK, row, c),
          };
        });

        return (
          <>
            {parentExpansions}
            <ExpandPanel sections={childSections} />
          </>
        );
      }}
    />
  );
}
